/**
 * RelatedBlogs — Feb 22, 2026
 * Renders 3-5 semantically similar published blogs at the bottom of
 * BlogDetailPage. Uses the backend endpoint `/api/blogs/{id}/related` which
 * ranks by category + Jaccard(title tokens) + Jaccard(excerpt tokens). No
 * self-linking. No LLM. Card styling mirrors the existing homepage blog grid
 * so mobile responsiveness is preserved.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RelatedBlogs({ blogId, currentSlug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!blogId) { setLoading(false); return; }
      try {
        const r = await axios.get(`${API}/api/blogs/${encodeURIComponent(blogId)}/related?limit=5`);
        if (!cancelled) setItems((r.data?.related || []).filter(b => b.slug !== currentSlug));
      } catch (e) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [blogId, currentSlug]);

  if (loading || items.length === 0) return null;

  return (
    <section
      className="mt-14 pt-10 border-t border-gold/20"
      data-testid="related-blogs-section"
      aria-labelledby="related-blogs-heading"
    >
      <h2
        id="related-blogs-heading"
        className="text-xs uppercase tracking-[0.28em] text-gold/80 font-bold mb-6"
      >
        संबंधित लेख — पढ़ना जारी रखें
      </h2>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="related-blogs-grid">
        {items.map(b => (
          <Link
            key={b.id}
            to={`/blog/${b.slug}`}
            className="group relative flex flex-col rounded-2xl overflow-hidden bg-white/[0.03] border border-gold/15 hover:border-gold/40 transition-all"
            data-testid={`related-card-${b.slug}`}
          >
            <div className="aspect-[16/9] overflow-hidden bg-purple-950/40">
              <img
                src={b.image_url || '/brand/og-cover.png'}
                alt={b.title}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-4 flex-1 flex flex-col">
              {b.category && (
                <div className="text-[10px] uppercase tracking-widest text-gold/70 mb-2">{b.category}</div>
              )}
              <h3 className="font-heading text-base text-text-main leading-snug group-hover:text-gold transition-colors line-clamp-2">
                {b.meta_title || b.title}
              </h3>
              {(b.meta_description || b.excerpt) && (
                <p className="text-xs text-text-muted mt-2 line-clamp-2">
                  {b.meta_description || b.excerpt}
                </p>
              )}
              <span className="mt-3 inline-flex items-center gap-1 text-[11px] text-gold/80 group-hover:text-gold">
                पढ़ें <ArrowUpRight className="w-3 h-3" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
